/* eslint-disable @typescript-eslint/no-explicit-any */
import { addBreadcrumb, installBreadcrumbAutoCapture, snapshotBreadcrumbs } from "./breadcrumbs";
import { scrubUrl, scrubValue } from "./scrub";
import { connectionInfo, installVitalsCapture } from "./vitals";

const SESSION_KEY = "fx_err_session_v1";
const QUEUE_KEY = "fx_err_queue_v1";
const OPT_OUT_KEY = "opt_out_error_tracking";
const ENDPOINT = "/api/public/client-errors";
const MAX_QUEUE_ATTEMPTS = 3;

let installed = false;
let lastKey = "";
let lastTs = 0;
let userId: string | null = null;
let userEmail: string | null = null;
// Track errors already reported in this mount to avoid re-report on router.invalidate()
const reportedInMount = new WeakSet<object>();

export type ClientErrorSource =
  | "render"
  | "unhandled"
  | "promise"
  | "network"
  | "checkout"
  | "cart"
  | "payment"
  | "query"
  | "mutation"
  | "manual";

export interface ClientErrorMeta {
  source?: ClientErrorSource;
  severity?: "error" | "warning";
  action?: string;
  context?: Record<string, unknown>;
  http_status?: number | null;
  server_fn?: string | null;
  showToast?: boolean;
}

export interface ReportedClientError {
  correlation_id: string;
  message: string;
  source: ClientErrorSource;
  severity: "error" | "warning";
}

function isOptedOut(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)) +
        "-" +
        Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "no-storage";
  }
}

function newCorrelationId(): string {
  const bytes = new Uint8Array(6);
  (globalThis.crypto ?? (window as any).crypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

/**
 * Fingerprint from top 3 non-vendor stack frames + source + normalized message.
 * More stable than "first at ..." across minified builds.
 */
function fingerprint(source: string, message: string, stack?: string | null, route?: string): string {
  const cleanMsg = message.replace(/\d+/g, "N").slice(0, 120);
  const frames = (stack ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("at ") && !/node_modules|react-dom|scheduler/.test(l))
    .slice(0, 3)
    .join("|");
  const raw = `${source}|${cleanMsg}|${frames.slice(0, 240)}|${route ?? ""}`;
  let h = 0;
  for (let i = 0; i < raw.length; i++) h = (h * 31 + raw.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

type QueuedPayload = Record<string, unknown> & { _attempts?: number };

function loadQueue(): QueuedPayload[] {
  try {
    return JSON.parse(sessionStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveQueue(items: QueuedPayload[]) {
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-30)));
  } catch {
    /* ignore */
  }
}

async function post(payload: Record<string, unknown>, keepalive = false): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive,
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function flushQueue() {
  const q = loadQueue();
  if (q.length === 0) return;
  const remaining: QueuedPayload[] = [];
  for (const p of q) {
    const ok = await post(p);
    if (!ok) {
      const attempts = (p._attempts ?? 0) + 1;
      if (attempts < MAX_QUEUE_ATTEMPTS) remaining.push({ ...p, _attempts: attempts });
    }
  }
  saveQueue(remaining);
}

export function setTrackerUser(id: string | null, email: string | null) {
  const wasSignedIn = !!userId;
  userId = id;
  userEmail = email;
  if (id && !wasSignedIn) addBreadcrumb("auth", "signed_in");
  else if (!id && wasSignedIn) addBreadcrumb("auth", "signed_out");
}

export function reportClientError(err: unknown, meta: ClientErrorMeta = {}): ReportedClientError | null {
  if (isOptedOut()) return null;
  if (typeof window === "undefined") return null;

  // Deduplicate same Error object being re-thrown via router.invalidate
  if (err && typeof err === "object" && reportedInMount.has(err as object)) return null;

  // Skip cancelled/aborted network errors — they aren't real bugs.
  const rawName = (err as any)?.name;
  if (rawName === "AbortError" || rawName === "CanceledError") return null;

  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : (err as any)?.message ?? String(err);
  const stack = err instanceof Error ? err.stack ?? null : null;
  const source = meta.source ?? "manual";
  const severity = meta.severity ?? "error";

  // sampling: errors 100%, warnings 25%
  if (severity === "warning" && Math.random() > 0.25) return null;

  // dedupe 5s
  const key = `${source}|${severity}|${message}`;
  const now = Date.now();
  if (key === lastKey && now - lastTs < 5000) return null;
  lastKey = key;
  lastTs = now;

  const route = scrubUrl(location.pathname + location.search);
  const url = scrubUrl(location.href);
  const correlation_id = newCorrelationId();
  const fp = fingerprint(source, message, stack, route);

  const payload = {
    session_id: getSessionId(),
    // user_email is IGNORED for signed-in users (server derives from JWT);
    // sent only as best-effort hint for guests.
    user_email: userId ? null : userEmail,
    correlation_id,
    source,
    severity,
    message: String(message).slice(0, 4000),
    stack: stack ? stack.slice(0, 20000) : null,
    route,
    url,
    referrer: scrubUrl(document.referrer || ""),
    http_status: meta.http_status ?? null,
    server_fn: meta.server_fn ?? null,
    context: scrubValue({
      action: meta.action,
      online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      connection: connectionInfo(),
      ...(meta.context ?? {}),
    }),
    breadcrumbs: snapshotBreadcrumbs(),
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    release: (window as any).__BUILD_ID__ ?? null,
    fingerprint: fp,
  };

  if (err && typeof err === "object") reportedInMount.add(err as object);

  post(payload).then((ok) => {
    if (!ok) {
      const q = loadQueue();
      q.push(payload);
      saveQueue(q);
    }
  });

  if (
    (meta.showToast ?? severity === "error") &&
    (source === "checkout" || source === "payment" || source === "cart")
  ) {
    void showCorrToast(correlation_id);
  }

  return { correlation_id, message: String(message), source, severity };
}

async function showCorrToast(corr: string) {
  try {
    const { toast } = await import("sonner");
    toast.error("Нещо се обърка. Опитайте отново.", {
      description: `Референция за поддръжка: #${corr}`,
      duration: 8000,
      action: {
        label: "Копирай",
        onClick: () => navigator.clipboard?.writeText(corr),
      },
    });
  } catch {
    /* ignore */
  }
}

export function trackCheckout(step: string, meta: Record<string, unknown> = {}) {
  addBreadcrumb("checkout", `checkout.${step}`, meta);
}
export function trackCart(action: string, meta: Record<string, unknown> = {}) {
  addBreadcrumb("cart", `cart.${action}`, meta);
}

export function installClientErrorTracker() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  installBreadcrumbAutoCapture();

  // ensure session id exists
  getSessionId();

  // uncaught JS errors + resource-loading errors (capture phase)
  window.addEventListener(
    "error",
    (event) => {
      // Resource loading error (script/img/link/media) — event.target is the element
      const t = event.target as unknown;
      if (
        t &&
        t !== window &&
        (t instanceof HTMLScriptElement ||
          t instanceof HTMLLinkElement ||
          t instanceof HTMLImageElement ||
          t instanceof HTMLMediaElement)
      ) {
        const tag = (t as HTMLElement).tagName.toLowerCase();
        const src =
          (t as HTMLScriptElement).src ||
          (t as HTMLLinkElement).href ||
          (t as HTMLImageElement).currentSrc ||
          "";
        reportClientError(new Error(`Resource load failed: <${tag}>`), {
          source: "network",
          severity: "warning",
          action: `resource.${tag}`,
          context: { src: scrubUrl(src) },
        });
        return;
      }
      const err = event.error ?? new Error(event.message || "Script error");
      reportClientError(err, { source: "unhandled", action: "window.onerror" });
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    reportClientError(reason ?? new Error("Unhandled promise rejection"), {
      source: "promise",
      action: "unhandledrejection",
    });
  });

  // Web Vitals — report poor scores as warnings
  installVitalsCapture((name, value, rating) => {
    if (rating === "poor") {
      reportClientError(new Error(`Poor ${name}: ${value}`), {
        source: "render",
        severity: "warning",
        action: `vitals.${name.toLowerCase()}`,
        context: { metric: name, value, rating },
      });
    }
  });

  // flush pending queue on load & on tab hide
  void flushQueue();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushQueue();
  });
  window.addEventListener("pagehide", () => {
    // send remaining as beacon (survives navigation)
    const q = loadQueue();
    for (const p of q) {
      try {
        const blob = new Blob([JSON.stringify(p)], { type: "application/json" });
        navigator.sendBeacon?.(ENDPOINT, blob);
      } catch {
        /* ignore */
      }
    }
    saveQueue([]);
  });
}

export function setTrackingOptOut(optOut: boolean) {
  try {
    if (optOut) {
      window.localStorage.setItem(OPT_OUT_KEY, "1");
    } else {
      window.localStorage.removeItem(OPT_OUT_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function isTrackingOptedOut(): boolean {
  return isOptedOut();
}
